/**
 * AI-Generated Executive Dashboard Report
 * Professional dashboard visualization for healthcare CXOs and operations staff
 * Renders Azure AI-generated dashboard specifications
 */

import React, { useRef, useState } from 'react';
import { Card, Row, Col, Tag, Progress, Statistic, Divider, Badge, Button, Tooltip, Typography, Space, Collapse } from 'antd';
import {
    DownloadOutlined,
    ReloadOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    MinusOutlined,
    DashboardOutlined,
    HeartOutlined,
    TeamOutlined,
    BarChartOutlined,
    AlertOutlined,
    CheckCircleOutlined,
    FireOutlined,
    WarningOutlined,
    BulbOutlined,
    ThunderboltOutlined,
    LineChartOutlined,
    InfoCircleOutlined,
    TrophyOutlined,
    RiseOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    ExclamationCircleOutlined,
    RocketOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import GaugeChart from 'react-gauge-chart';
import { useUser } from 'components/UserContext';
import AI_FEATURE_KEYS from 'constants/aiFeatureKeys';

const { Title, Text, Paragraph } = Typography;

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
    'dashboard': DashboardOutlined,
    'heart': HeartOutlined,
    'users': TeamOutlined,
    'chart': BarChartOutlined,
    'alert': AlertOutlined,
    'check': CheckCircleOutlined,
    'fire': FireOutlined,
    'warning': WarningOutlined,
    'info': InfoCircleOutlined,
    'bulb': BulbOutlined,
    'thunder': ThunderboltOutlined,
    'line-chart': LineChartOutlined,
    'trophy': TrophyOutlined,
    'trend': RiseOutlined
};

// Styled Components for Professional Layout
const DashboardContainer = styled.div`
    background: ${props => props.theme?.backgroundColor || '#fafafa'};
    padding: 20px;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const DashboardHeader = styled.div`
    background: linear-gradient(135deg, ${props => props.theme?.primaryGradient?.[0] || '#667eea'} 0%, ${props => props.theme?.primaryGradient?.[1] || '#764ba2'} 100%);
    padding: 24px 28px;
    border-radius: 16px;
    color: white;
    margin-bottom: 24px;
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -10%;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
        border-radius: 50%;
    }

    h1 {
        color: white;
        margin: 0 0 8px 0;
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.3px;
        position: relative;
        z-index: 1;
    }

    p {
        color: rgba(255, 255, 255, 0.95);
        margin: 0 0 12px 0;
        font-size: 14px;
        font-weight: 500;
        position: relative;
        z-index: 1;
    }

    .executive-summary {
        color: rgba(255, 255, 255, 0.9);
        font-size: 13px;
        line-height: 1.6;
        margin-top: 12px;
        padding: 14px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 10px;
        border-left: 3px solid rgba(255, 255, 255, 0.5);
        backdrop-filter: blur(10px);
        position: relative;
        z-index: 1;
    }
`;

const ScorecardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
`;

const MetricCard = styled(Card)`
    border-radius: 12px;
    border: none;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    position: relative;
    background: white;
    height: 100%;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${props => props.borderColor || 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'};
    }

    &:hover {
        transform: translateY(-6px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    }

    .ant-card-body {
        padding: 20px 18px;
    }

    .metric-icon {
        font-size: 32px;
        color: ${props => props.iconColor || '#667eea'};
        margin-bottom: 12px;
        opacity: 0.9;
    }

    .metric-label {
        font-size: 11px;
        font-weight: 700;
        color: #8c8c8c;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }

    .metric-value {
        font-size: ${props => props.large ? '36px' : '32px'};
        font-weight: 800;
        color: ${props => props.valueColor || '#262626'};
        line-height: 1;
        margin-bottom: 8px;
    }

    .metric-trend {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 12px;
        background: ${props =>
            props.trendType === 'up' ? '#f6ffed' :
            props.trendType === 'down' ? '#fff2e8' :
            '#f0f0f0'
        };
        color: ${props =>
            props.trendType === 'up' ? '#52c41a' :
            props.trendType === 'down' ? '#fa8c16' :
            '#8c8c8c'
        };
        font-size: 12px;
        font-weight: 600;
        margin-top: 6px;
    }

    .metric-insight {
        font-size: 12px;
        color: #595959;
        line-height: 1.5;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #f0f0f0;
    }
`;

const SectionContainer = styled.div`
    margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: #262626;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
        content: '';
        width: 4px;
        height: 24px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 2px;
    }
`;

const InsightCard = styled(Card)`
    border-radius: 12px;
    border: 2px solid ${props => props.borderColor || '#f0f0f0'};
    background: ${props => props.background || 'white'};
    box-shadow: 0 3px 12px ${props => props.shadowColor || 'rgba(0, 0, 0, 0.05)'};
    transition: all 0.3s ease;
    margin-bottom: 12px;
    height: 100%;

    &:hover {
        box-shadow: 0 6px 20px ${props => props.shadowColor || 'rgba(0, 0, 0, 0.1)'};
        transform: translateY(-3px);
    }

    .ant-card-head {
        background: ${props => props.headerBg || 'transparent'};
        border-bottom: 2px solid ${props => props.borderColor || '#f0f0f0'};
        padding: 14px 18px;
        border-radius: 10px 10px 0 0;
        min-height: auto;

        .ant-card-head-title {
            font-size: 14px;
            font-weight: 700;
            color: ${props => props.titleColor || '#262626'};
        }
    }

    .ant-card-body {
        padding: 16px 18px;
        font-size: 13px;
        line-height: 1.6;
    }
`;

const RecommendationCard = styled(Card)`
    border-radius: 12px;
    border-left: 4px solid ${props => props.borderColor || '#667eea'};
    background: ${props => props.background || 'white'};
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
    margin-bottom: 12px;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
        transform: translateX(6px);
    }

    .ant-card-body {
        padding: 16px 18px;
    }

    .rec-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 10px;
    }

    .rec-title {
        font-size: 14px;
        font-weight: 700;
        color: #262626;
        line-height: 1.4;
        flex: 1;
    }

    .rec-content {
        font-size: 12px;
        color: #595959;
        line-height: 1.6;
        margin-bottom: 10px;
    }

    .rec-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding-top: 10px;
        border-top: 1px solid #f0f0f0;
        font-size: 11px;
    }
`;

const ExportButton = styled(Button)`
    position: fixed;
    bottom: 32px;
    right: 32px;
    z-index: 1000;
    height: 56px;
    padding: 0 32px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 28px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(102, 126, 234, 0.5);
        background: linear-gradient(135deg, #7c8ef5 0%, #8759b3 100%);
    }
`;

const ChartContainer = styled.div`
    background: white;
    padding: 32px;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    margin-bottom: 24px;
    height: 100%;

    .chart-title {
        font-size: 20px;
        font-weight: 700;
        color: #262626;
        margin-bottom: 8px;
    }

    .chart-subtitle {
        font-size: 14px;
        color: #8c8c8c;
        margin-bottom: 24px;
    }

    .chart-insight {
        font-size: 14px;
        color: #595959;
        line-height: 1.6;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #f0f0f0;
        font-style: italic;
    }
`;

const TextSummarySection = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px 28px;
    margin-bottom: 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border-left: 4px solid #667eea;

    .summary-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 20px;
        font-size: 18px;
        font-weight: 700;
        color: #262626;
    }

    .summary-section {
        margin-bottom: 20px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .section-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
        color: #262626;
        margin-bottom: 12px;
    }

    .summary-item {
        padding: 12px 16px;
        background: #fafafa;
        border-radius: 8px;
        margin-bottom: 8px;
        font-size: 13px;
        line-height: 1.6;
        color: #595959;
        border-left: 3px solid #e6e6e6;
        transition: all 0.2s ease;

        &:hover {
            background: #f0f0f0;
            border-left-color: #667eea;
        }

        &.metric {
            border-left-color: #FF7F3F;
            background: #fff7f0;
        }

        &.insight {
            border-left-color: #52c41a;
            background: #f6ffed;
        }

        &.concern {
            border-left-color: #faad14;
            background: #fffbe6;
        }

        &.recommendation {
            border-left-color: #1890ff;
            background: #e6f7ff;
        }
    }

    .metric-label {
        font-weight: 600;
        color: #262626;
        margin-right: 8px;
    }

    .metric-value {
        font-weight: 700;
        color: #FF7F3F;
    }
`;

export default function AIDashboardReport({
    dashboardSpec,
    summary = null,
    cacheMetadata = null,
    onRegenerate = null,
    isRegenerating = false
}) {
    const { hasFeatureAccess } = useUser();
    const dashboardRef = useRef(null);
    const canExportExecutiveReport = hasFeatureAccess(AI_FEATURE_KEYS.EXEC_REPORT_EXPORT);

    if (!dashboardSpec) {
        return null;
    }

    const theme = dashboardSpec.theme || {};
    const scorecard = dashboardSpec.scorecard || {};
    const keyFindings = dashboardSpec.keyFindings || [];
    const trendsAnalysis = dashboardSpec.trendsAnalysis || [];
    const insights = dashboardSpec.insights || [];
    const charts = dashboardSpec.charts || [];
    const recommendations = dashboardSpec.recommendations || {};
    const layout = dashboardSpec.layout || {};

    // Helper to format cache age
    const formatCacheAge = (seconds) => {
        if (!seconds) return 'Just generated';
        if (seconds < 60) return `${seconds}s ago`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        return `${Math.floor(seconds / 3600)}h ago`;
    };

    // Helper to get cache badge color
    const getCacheBadgeColor = () => {
        if (!cacheMetadata?.isFromCache) return null;
        if (cacheMetadata.isExpired) return 'warning';
        if (cacheMetadata.cacheAge < 600) return 'success'; // < 10 minutes
        if (cacheMetadata.cacheAge < 1800) return 'processing'; // < 30 minutes
        return 'default';
    };

    // Helper function to get icon component
    const getIcon = (iconName) => {
        const IconComponent = ICON_MAP[iconName] || DashboardOutlined;
        return <IconComponent />;
    };

    // Helper function to get trend icon
    const getTrendIcon = (trend) => {
        if (trend === 'up') return <ArrowUpOutlined />;
        if (trend === 'down') return <ArrowDownOutlined />;
        return <MinusOutlined />;
    };

    // Helper function to get severity color
    const getSeverityColor = (severity) => {
        const colors = {
            'critical': theme.criticalColor || '#ff4d4f',
            'high': theme.warningColor || '#faad14',
            'medium': theme.infoColor || '#1890ff',
            'low': theme.successColor || '#52c41a',
            'info': theme.infoColor || '#1890ff'
        };
        return colors[severity] || colors.info;
    };

    // Helper function to get priority color
    const getPriorityColor = (priority) => {
        const colors = {
            'critical': theme.criticalColor || '#ff4d4f',
            'high': theme.warningColor || '#faad14',
            'medium': theme.infoColor || '#1890ff',
            'low': theme.successColor || '#52c41a'
        };
        return colors[priority] || colors.medium;
    };

    // Export dashboard as image
    const handleExport = async () => {
        if (typeof window === 'undefined') return;

        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(dashboardRef.current, {
                backgroundColor: theme.backgroundColor || '#fafafa',
                scale: 2,
                logging: false,
                useCORS: true
            });

            const link = document.createElement('a');
            link.download = `${dashboardSpec.dashboardTitle?.replace(/\s+/g, '_') || 'dashboard'}_${new Date().toISOString().split('T')[0]}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error('Error exporting dashboard:', error);
        }
    };

    return (
        <>
            <DashboardContainer ref={dashboardRef} theme={theme}>
                {/* Header Controls (Cache Status + Regenerate Button) */}
                {(cacheMetadata || onRegenerate) && (
                    <div style={{
                        position: 'sticky',
                        top: 20,
                        zIndex: 100,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: '-56px',
                        paddingRight: '8px'
                    }}>
                        <Space size="middle">
                            {/* Cache Status Badge */}
                            {cacheMetadata && cacheMetadata.isFromCache && (
                                <Tooltip title={`Generated at ${new Date(cacheMetadata.generatedAt).toLocaleTimeString()}${cacheMetadata.isExpired ? ' (expired)' : ''}`}>
                                    <Tag
                                        color={getCacheBadgeColor()}
                                        icon={<ClockCircleOutlined />}
                                        style={{
                                            fontSize: '12px',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            border: 'none',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {formatCacheAge(cacheMetadata.cacheAge)}
                                    </Tag>
                                </Tooltip>
                            )}

                            {/* Regenerate Button */}
                            {onRegenerate && (
                                <Tooltip title="Regenerate dashboard with latest data">
                                    <Button
                                        type="primary"
                                        icon={<ReloadOutlined spin={isRegenerating} />}
                                        onClick={onRegenerate}
                                        loading={isRegenerating}
                                        style={{
                                            borderRadius: '16px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                    >
                                        {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                                    </Button>
                                </Tooltip>
                            )}
                        </Space>
                    </div>
                )}

                {/* Header Section */}
                <DashboardHeader theme={theme}>
                    <h1>{dashboardSpec.dashboardTitle}</h1>
                    <p>{dashboardSpec.subtitle}</p>
                    {dashboardSpec.executiveSummary && (
                        <div className="executive-summary">
                            <strong>Executive Summary:</strong> {dashboardSpec.executiveSummary}
                        </div>
                    )}
                </DashboardHeader>

                {/* Text Summary Section (from Compact Mode) */}
                {summary && (
                    <Collapse
                        defaultActiveKey={['text-summary']}
                        style={{ marginBottom: 24, borderRadius: 16, overflow: 'hidden' }}
                        items={[
                            {
                                key: 'text-summary',
                                label: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 16 }}>
                                        <FileTextOutlined style={{ color: '#667eea' }} />
                                        Detailed Analysis Summary
                                        <Badge count={(summary.keyMetrics?.length || 0) + (summary.keyInsights?.length || 0) + (summary.areasOfConcern?.length || 0) + (summary.recommendations?.length || 0)} style={{ marginLeft: 8, background: '#667eea' }} />
                                    </div>
                                ),
                                children: (
                                    <TextSummarySection>
                                        {/* Key Metrics */}
                                        {summary.keyMetrics && summary.keyMetrics.length > 0 && (
                                            <div className="summary-section">
                                                <div className="section-title">
                                                    <BarChartOutlined style={{ color: '#FF7F3F' }} />
                                                    Key Metrics ({summary.keyMetrics.length})
                                                </div>
                                                {summary.keyMetrics.map((metric, index) => (
                                                    <div key={index} className="summary-item metric">
                                                        <span className="metric-label">{metric.metric || metric.label}:</span>
                                                        <span className="metric-value">{metric.value}</span>
                                                        {metric.context && <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>{metric.context}</div>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Key Insights */}
                                        {summary.keyInsights && summary.keyInsights.length > 0 && (
                                            <div className="summary-section">
                                                <div className="section-title">
                                                    <BulbOutlined style={{ color: '#52c41a' }} />
                                                    Key Insights ({summary.keyInsights.length})
                                                </div>
                                                {summary.keyInsights.map((insight, index) => (
                                                    <div key={index} className="summary-item insight">
                                                        {typeof insight === 'string' ? insight : insight.insight || insight.text}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Areas of Concern */}
                                        {summary.areasOfConcern && summary.areasOfConcern.length > 0 && (
                                            <div className="summary-section">
                                                <div className="section-title">
                                                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                                    Areas of Concern ({summary.areasOfConcern.length})
                                                </div>
                                                {summary.areasOfConcern.map((concern, index) => (
                                                    <div key={index} className="summary-item concern">
                                                        {typeof concern === 'string' ? concern : concern.concern || concern.text}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Recommendations */}
                                        {summary.recommendations && summary.recommendations.length > 0 && (
                                            <div className="summary-section">
                                                <div className="section-title">
                                                    <RocketOutlined style={{ color: '#1890ff' }} />
                                                    Recommendations ({summary.recommendations.length})
                                                </div>
                                                {summary.recommendations.map((rec, index) => (
                                                    <div key={index} className="summary-item recommendation">
                                                        {typeof rec === 'string' ? rec : rec.recommendation || rec.text}
                                                        {rec.priority && (
                                                            <Tag color={rec.priority === 'high' ? 'red' : rec.priority === 'medium' ? 'orange' : 'blue'} style={{ marginLeft: 8 }}>
                                                                {rec.priority}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </TextSummarySection>
                                )
                            }
                        ]}
                    />
                )}

                {/* Executive Scorecard */}
                {scorecard.metrics && scorecard.metrics.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>{scorecard.title || 'Executive Scorecard'}</SectionTitle>
                        <ScorecardGrid>
                            {scorecard.metrics.map((metric, index) => (
                                <MetricCard
                                    key={metric.id || index}
                                    borderColor={metric.color}
                                    iconColor={metric.color}
                                    valueColor={metric.color}
                                    trendType={metric.trend}
                                    large={metric.size === 'large'}
                                >
                                    <div className="metric-icon">{getIcon(metric.icon)}</div>
                                    <div className="metric-label">{metric.label}</div>
                                    <div className="metric-value">
                                        {metric.value}
                                        {metric.unit && <span style={{ fontSize: '0.5em', marginLeft: '4px' }}>{metric.unit}</span>}
                                    </div>
                                    {metric.trend && (
                                        <div className="metric-trend">
                                            {getTrendIcon(metric.trend)}
                                            <span>{metric.trendValue}</span>
                                            {metric.comparison && <span style={{ fontSize: '12px', opacity: 0.8 }}>{metric.comparison}</span>}
                                        </div>
                                    )}
                                    {metric.insight && (
                                        <div className="metric-insight">{metric.insight}</div>
                                    )}
                                </MetricCard>
                            ))}
                        </ScorecardGrid>
                    </SectionContainer>
                )}

                {/* Key Insights */}
                {dashboardSpec.keyInsights && dashboardSpec.keyInsights.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>
                            <BulbOutlined /> Key Insights ({dashboardSpec.keyInsights.length})
                        </SectionTitle>
                        <Row gutter={[16, 16]}>
                            {dashboardSpec.keyInsights.map((insight, index) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={insight.id || index}>
                                    <InsightCard
                                        title={
                                            <span style={{ fontSize: '13px' }}>
                                                <BulbOutlined /> {insight.title}
                                            </span>
                                        }
                                        borderColor="#52c41a"
                                        shadowColor="#52c41a40"
                                        headerBg="#52c41a10"
                                        titleColor="#52c41a"
                                        extra={
                                            <Tag color="success" style={{ fontSize: '10px', padding: '2px 8px' }}>
                                                INSIGHT
                                            </Tag>
                                        }
                                    >
                                        <Paragraph style={{ marginBottom: 0, fontSize: '12px' }}>{insight.description}</Paragraph>
                                    </InsightCard>
                                </Col>
                            ))}
                        </Row>
                    </SectionContainer>
                )}

                {/* Areas of Concern */}
                {dashboardSpec.areasOfConcern && dashboardSpec.areasOfConcern.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>
                            <WarningOutlined /> Areas of Concern ({dashboardSpec.areasOfConcern.length})
                        </SectionTitle>
                        <Row gutter={[16, 16]}>
                            {dashboardSpec.areasOfConcern.map((concern, index) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={concern.id || index}>
                                    <InsightCard
                                        title={
                                            <span style={{ fontSize: '13px' }}>
                                                <WarningOutlined /> {concern.title}
                                            </span>
                                        }
                                        borderColor={concern.severity === 'high' ? '#ff4d4f' : '#faad14'}
                                        shadowColor={concern.severity === 'high' ? '#ff4d4f40' : '#faad1440'}
                                        headerBg={concern.severity === 'high' ? '#ff4d4f10' : '#faad1410'}
                                        titleColor={concern.severity === 'high' ? '#ff4d4f' : '#faad14'}
                                        extra={
                                            <Tag color={concern.severity === 'high' ? 'error' : 'warning'} style={{ fontSize: '10px', padding: '2px 8px' }}>
                                                {concern.severity?.toUpperCase()}
                                            </Tag>
                                        }
                                    >
                                        <Paragraph style={{ marginBottom: 10, fontSize: '12px' }}>{concern.description}</Paragraph>
                                        {concern.actionRequired && (
                                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0', fontSize: '11px', color: '#8c8c8c' }}>
                                                <strong>Action:</strong> {concern.actionRequired}
                                            </div>
                                        )}
                                    </InsightCard>
                                </Col>
                            ))}
                        </Row>
                    </SectionContainer>
                )}

                {/* Custom Sections */}
                {dashboardSpec.customSections && dashboardSpec.customSections.length > 0 && (
                    dashboardSpec.customSections.map((section, sectionIndex) => (
                        <SectionContainer key={section.id || sectionIndex}>
                            <SectionTitle>
                                <InfoCircleOutlined /> {section.title}
                                {section.priority && (
                                    <Tag color={section.priority === 'high' ? 'error' : section.priority === 'medium' ? 'warning' : 'default'} style={{ marginLeft: 8 }}>
                                        {section.priority.toUpperCase()}
                                    </Tag>
                                )}
                            </SectionTitle>
                            <div style={{ padding: '20px', background: '#fafafa', borderRadius: '12px' }}>
                                {/* Show description if available */}
                                {section.content?.description && (
                                    <Paragraph style={{ marginBottom: 16, fontSize: '14px', color: '#595959' }}>
                                        {section.content.description}
                                    </Paragraph>
                                )}

                                {/* Render cards array (new format from backend transformation) */}
                                {section.content && section.content.cards && section.content.cards.length > 0 && (
                                    <Row gutter={[16, 16]}>
                                        {section.content.cards.map((card, cardIdx) => (
                                            <Col xs={24} sm={12} md={8} lg={6} key={card.id || cardIdx}>
                                                <div style={{
                                                    padding: '20px',
                                                    background: '#fff',
                                                    borderRadius: '12px',
                                                    border: `2px solid ${card.color || '#1890ff'}20`,
                                                    boxShadow: `0 2px 8px ${card.color || '#1890ff'}15`,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}>
                                                    {/* Value and Percentage - Large Display */}
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                                                        {card.value && (
                                                            <div style={{
                                                                fontSize: '32px',
                                                                fontWeight: '700',
                                                                color: card.color || '#1890ff'
                                                            }}>
                                                                {card.value}
                                                            </div>
                                                        )}
                                                        {card.percentage && (
                                                            <div style={{
                                                                fontSize: '20px',
                                                                fontWeight: '600',
                                                                color: '#8c8c8c'
                                                            }}>
                                                                ({card.percentage})
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Label - Bold */}
                                                    <Text strong style={{ fontSize: '14px', color: '#262626', marginBottom: '8px', display: 'block' }}>
                                                        {card.label}
                                                    </Text>

                                                    {/* Description - Secondary */}
                                                    {card.description && (
                                                        <Paragraph style={{ marginBottom: 0, fontSize: '12px', color: '#8c8c8c', lineHeight: '1.5' }}>
                                                            {card.description}
                                                        </Paragraph>
                                                    )}

                                                    {/* Trend Indicator if available */}
                                                    {card.trend && (
                                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                                                            {card.trend === 'up' && '↑ Increasing'}
                                                            {card.trend === 'down' && '↓ Decreasing'}
                                                            {card.trend === 'stable' && '→ Stable'}
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                )}

                                {/* Legacy: Render table type if old format */}
                                {section.type === 'table' && section.content && section.content.rows && (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    {section.content.headers && section.content.headers.map((header, idx) => (
                                                        <th key={idx} style={{ padding: '12px', borderBottom: '2px solid #d9d9d9', textAlign: 'left', fontWeight: '600' }}>
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {section.content.rows.map((row, rowIdx) => (
                                                    <tr key={rowIdx}>
                                                        {row.map((cell, cellIdx) => (
                                                            <td key={cellIdx} style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                                                {cell}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Legacy: Render breakdown type items if old format */}
                                {section.type === 'breakdown' && section.content && section.content.items && !section.content.cards && (
                                    <Row gutter={[16, 16]}>
                                        {section.content.items.map((item, itemIdx) => (
                                            <Col xs={24} sm={12} md={8} key={itemIdx}>
                                                <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                                                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#F16622', marginBottom: '8px' }}>
                                                        {item.value || item}
                                                    </div>
                                                    <Text strong>{item.label || `Item ${itemIdx + 1}`}</Text>
                                                    {item.description && (
                                                        <Paragraph style={{ marginTop: 8, marginBottom: 0, fontSize: '12px', color: '#8c8c8c' }}>
                                                            {item.description}
                                                        </Paragraph>
                                                    )}
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                )}

                                {/* Fallback: If no cards and no recognized format, show raw content */}
                                {!section.content?.cards && section.type !== 'table' && section.type !== 'breakdown' && section.content && typeof section.content !== 'string' && (
                                    <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontSize: '12px' }}>
                                            {JSON.stringify(section.content, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </SectionContainer>
                    ))
                )}

                {/* Key Findings */}
                {keyFindings && keyFindings.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>
                            <AlertOutlined /> Key Findings ({keyFindings.length})
                        </SectionTitle>
                        <Row gutter={[16, 16]}>
                            {keyFindings.map((finding, index) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={finding.id || index}>
                                    <InsightCard
                                        title={
                                            <span style={{ fontSize: '13px' }}>
                                                {getIcon(finding.icon)} {finding.title}
                                            </span>
                                        }
                                        borderColor={getSeverityColor(finding.severity)}
                                        shadowColor={`${getSeverityColor(finding.severity)}40`}
                                        headerBg={`${getSeverityColor(finding.severity)}10`}
                                        titleColor={getSeverityColor(finding.severity)}
                                        extra={
                                            <Tag color={getSeverityColor(finding.severity)} style={{ fontSize: '10px', padding: '2px 8px' }}>
                                                {finding.severity?.toUpperCase()}
                                            </Tag>
                                        }
                                    >
                                        <Paragraph style={{ marginBottom: finding.impact ? 10 : 0, fontSize: '12px' }}>{finding.description}</Paragraph>
                                        {finding.impact && (
                                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0', fontSize: '11px', color: '#8c8c8c' }}>
                                                <strong>Impact:</strong> {finding.impact}
                                            </div>
                                        )}
                                        {finding.metric && (
                                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                                                <Text strong style={{ fontSize: '11px' }}>{finding.metric.label}:</Text>
                                                <Text style={{ fontSize: '11px', marginLeft: 4 }}>{finding.metric.value}</Text>
                                            </div>
                                        )}
                                    </InsightCard>
                                </Col>
                            ))}
                        </Row>
                    </SectionContainer>
                )}

                {/* Trends Analysis */}
                {trendsAnalysis && trendsAnalysis.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>
                            <LineChartOutlined /> Trends Analysis ({trendsAnalysis.length})
                        </SectionTitle>
                        <Row gutter={[16, 16]}>
                            {trendsAnalysis.map((trend, index) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={trend.id || index}>
                                    <InsightCard
                                        title={
                                            <span style={{ fontSize: '13px' }}>
                                                {getIcon(trend.icon)} {trend.title}
                                            </span>
                                        }
                                        borderColor={trend.direction === 'up' ? '#52c41a' : trend.direction === 'down' ? '#ff4d4f' : '#1890ff'}
                                        shadowColor={trend.direction === 'up' ? '#52c41a40' : trend.direction === 'down' ? '#ff4d4f40' : '#1890ff40'}
                                        headerBg={trend.direction === 'up' ? '#f6ffed' : trend.direction === 'down' ? '#fff2e8' : '#e6f7ff'}
                                        titleColor={trend.direction === 'up' ? '#52c41a' : trend.direction === 'down' ? '#ff4d4f' : '#1890ff'}
                                        extra={
                                            <Tag color={trend.direction === 'up' ? 'success' : trend.direction === 'down' ? 'error' : 'processing'} style={{ fontSize: '10px', padding: '2px 8px' }}>
                                                {trend.direction === 'up' ? '↑ UP' : trend.direction === 'down' ? '↓ DOWN' : '→ STABLE'}
                                            </Tag>
                                        }
                                    >
                                        <Paragraph style={{ marginBottom: 10, fontSize: '12px' }}>{trend.description}</Paragraph>
                                        {trend.interpretation && (
                                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0', fontSize: '11px', fontStyle: 'italic', color: '#595959' }}>
                                                {trend.interpretation}
                                            </div>
                                        )}
                                    </InsightCard>
                                </Col>
                            ))}
                        </Row>
                    </SectionContainer>
                )}

                {/* Charts & Visualizations */}
                {charts && charts.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>
                            <LineChartOutlined /> Performance Analytics
                        </SectionTitle>
                        <Row gutter={[24, 24]}>
                            {charts.map((chart, index) => (
                                <Col xs={24} sm={24} md={chart.type === 'gauge' ? 12 : 24} lg={chart.type === 'gauge' ? 12 : 12} key={chart.id || index}>
                                    <ChartContainer>
                                        <div className="chart-title">{chart.title}</div>
                                        {chart.subtitle && <div className="chart-subtitle">{chart.subtitle}</div>}

                                        {/* Render different chart types */}
                                        {chart.type === 'bar' && chart.data && (
                                            <div style={{ marginTop: 20 }}>
                                                {chart.data.map((item, i) => (
                                                    <div key={i} style={{ marginBottom: 16 }}>
                                                        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text strong>{item.name || item.label}</Text>
                                                            <Text>{item.value}</Text>
                                                        </div>
                                                        <Progress
                                                            percent={item.value}
                                                            strokeColor={item.color || theme.primaryGradient?.[0] || '#667eea'}
                                                            showInfo={false}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {chart.type === 'gauge' && chart.data && chart.data[0] && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
                                                <GaugeChart
                                                    id={`gauge-${index}`}
                                                    nrOfLevels={4}
                                                    colors={['#ff4d4f', '#faad14', '#fadb14', '#52c41a']}
                                                    arcWidth={0.3}
                                                    percent={chart.data[0].value / 100}
                                                    textColor={chart.data[0].color || '#262626'}
                                                    needleColor="#666"
                                                    needleBaseColor="#999"
                                                    hideText={true}
                                                    style={{ width: '300px' }}
                                                />
                                                <div style={{ marginTop: '-20px', textAlign: 'center' }}>
                                                    <div style={{ fontSize: '48px', fontWeight: '900', color: chart.data[0].color || '#262626' }}>
                                                        {chart.data[0].value}
                                                    </div>
                                                    <Text type="secondary">{chart.data[0].name || chart.data[0].label}</Text>
                                                </div>
                                            </div>
                                        )}

                                        {chart.type === 'donut' && chart.data && (
                                            <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                                                {chart.data.map((item, i) => (
                                                    <Col span={12} key={i}>
                                                        <div style={{ textAlign: 'center', padding: '16px', background: '#fafafa', borderRadius: '12px' }}>
                                                            <div style={{ fontSize: '32px', fontWeight: '700', color: item.color, marginBottom: '8px' }}>
                                                                {item.value}
                                                            </div>
                                                            <Text>{item.name || item.label}</Text>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}

                                        {chart.insight && (
                                            <div className="chart-insight">
                                                <InfoCircleOutlined style={{ marginRight: 8 }} />
                                                {chart.insight}
                                            </div>
                                        )}
                                    </ChartContainer>
                                </Col>
                            ))}
                        </Row>
                    </SectionContainer>
                )}

                {/* Strategic Action Plan */}
                {recommendations.items && recommendations.items.length > 0 && (
                    <SectionContainer>
                        <SectionTitle>
                            <ThunderboltOutlined /> {recommendations.title || 'Strategic Action Plan'} ({recommendations.items.length})
                        </SectionTitle>
                        <Row gutter={[16, 16]}>
                            {recommendations.items.map((rec, index) => {
                                const priorityColor = getPriorityColor(rec.priority);
                                return (
                                    <Col xs={24} sm={24} md={12} lg={8} key={rec.id || index}>
                                        <RecommendationCard
                                            borderColor={priorityColor}
                                            background={`${priorityColor}05`}
                                        >
                                            <div className="rec-header">
                                                <div className="rec-title">{rec.action}</div>
                                                <Tag color={priorityColor} style={{ fontSize: '10px', fontWeight: '600', padding: '2px 8px' }}>
                                                    {rec.priority?.toUpperCase() || 'MED'}
                                                </Tag>
                                            </div>

                                            {rec.rationale && (
                                                <div className="rec-content">
                                                    <Text strong style={{ fontSize: '11px' }}>Why: </Text>
                                                    {rec.rationale}
                                                </div>
                                            )}

                                            {rec.expectedOutcome && (
                                                <div className="rec-content">
                                                    <Text strong style={{ fontSize: '11px' }}>Outcome: </Text>
                                                    {rec.expectedOutcome}
                                                </div>
                                            )}

                                            <div className="rec-meta">
                                                {rec.timeline && (
                                                    <Tag icon={<ClockCircleOutlined />} style={{ fontSize: '10px', padding: '2px 6px' }}>{rec.timeline}</Tag>
                                                )}
                                                {rec.impact && (
                                                    <Tag color={rec.impact === 'high' ? 'red' : rec.impact === 'medium' ? 'orange' : 'green'} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                                        {rec.impact}
                                                    </Tag>
                                                )}
                                                {rec.category && (
                                                    <Tag style={{ fontSize: '10px', padding: '2px 6px' }}>{rec.category}</Tag>
                                                )}
                                            </div>
                                        </RecommendationCard>
                                    </Col>
                                );
                            })}
                        </Row>
                    </SectionContainer>
                )}

                {/* Footer Metadata */}
                {dashboardSpec.metadata && (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#8c8c8c', borderTop: '1px solid #f0f0f0' }}>
                        <Text type="secondary">
                            {dashboardSpec.exportOptions?.footer || 'Confidential - For Internal Use Only'}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Generated on {new Date(dashboardSpec.metadata.generatedAt).toLocaleString()} |
                            Module: {dashboardSpec.metadata.moduleName} |
                            Health Score: {dashboardSpec.metadata.healthScore}
                        </Text>
                    </div>
                )}
            </DashboardContainer>

            {/* Export Button */}
            {canExportExecutiveReport && (
                <Tooltip title="Download dashboard as image" placement="left">
                    <ExportButton
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={handleExport}
                    >
                        Export Dashboard
                    </ExportButton>
                </Tooltip>
            )}
        </>
    );
}
